// Configuración → Plan con facturación por marca, y el borrado de una marca con plan
// (ADR-2026-10-28-plan-por-marca-en-configuracion). Puro: sin base ni React.

import type { EstadoDeMarca } from '../../lib/paymentServer';
import { disponiblesDeEstadoDeMarca } from '../../lib/saldo';
import { esPlanDeMarca, nombreDePlanDeMarca } from './planDeMarca';
import { estadoDeCobroDeBilletera, type BilleteraParaCobro, type CobroPendiente } from '../../lib/estadoDeCobro';
import { recargaConBaja, recargaDeEstado, type RecargaDeMarca } from './recargaDeMarca';

export interface MarcaParaPlan {
  id: string;
  name: string;
  is_default?: boolean;
}

export interface FilaDePlanDeMarca {
  brandId: string;
  nombre: string;
  /** Nombre del plan, o `null` si la marca no tiene plan (con o sin billetera). */
  plan: string | null;
  tienePlan: boolean;
  disponibles: number;
  /** Fecha ISO a mostrar: la baja programada si existe, si no la próxima renovación. */
  fecha: string | null;
  fechaEs: 'baja' | 'renovacion' | null;
  cobroRechazado: boolean;
  /** "Cancelar" sólo con plan y sin baja ya programada. */
  puedeCancelar: boolean;
  /**
   * El cobro rechazado de la marca, con motivo, tarjeta y fechas (de su billetera; `null` si no
   * hay o no se pudo leer). ADR-2026-10-29-cobro-rechazado-por-marca.
   */
  cobro: CobroPendiente | null;
  /**
   * "Cambiar tarjeta": con plan y la suscripción viva en Rebill. Una baja PEDIDA deja la sub pausada
   * (no hay tarjeta que cambiar); la baja por morosidad sí se regulariza cambiándola.
   */
  puedeCambiarTarjeta: boolean;
  /** Hay un cobro pendiente: Rebill lo cobra apenas se cambia la tarjeta ("Cambiar tarjeta y pagar"). */
  cobroPendiente: boolean;
  /**
   * La recarga automática de créditos, sólo con plan y si el servidor la informa
   * (ADR-2026-10-29-recarga-automatica-de-marca). `null` = no se muestra.
   */
  recarga: RecargaDeMarca | null;
}

/**
 * Una fila por marca (ya filtradas las borradores), con lo que dice el payment server. Las
 * billeteras (lectura por RLS, opcional) traen el detalle del cobro rechazado; sin ellas, la fila
 * sólo sabe si hay un rechazo y no ofrece cambiar la tarjeta durante una baja.
 */
export function filasDePlanPorMarca(
  marcas: MarcaParaPlan[],
  estados: EstadoDeMarca[],
  billeteras: ReadonlyMap<string, BilleteraParaCobro> = new Map(),
  ahora: Date = new Date(),
): FilaDePlanDeMarca[] {
  const porMarca = new Map(estados.map((e) => [e.brand_id, e]));
  return marcas.map((m) => {
    const e = porMarca.get(m.id);
    const tienePlan = !!e && esPlanDeMarca(e.plan);
    const baja = tienePlan ? (e?.fecha_cancelacion_programada ?? null) : null;
    const renov = tienePlan ? (e?.proxima_renovacion ?? null) : null;
    const b = billeteras.get(m.id);
    const cobro = tienePlan && b ? estadoDeCobroDeBilletera(b, ahora) : null;
    const cobroRechazado = tienePlan && !!e?.cobro_rechazado_at;
    return {
      brandId: m.id,
      nombre: m.name,
      plan: tienePlan && e && esPlanDeMarca(e.plan) ? nombreDePlanDeMarca(e.plan) : null,
      tienePlan,
      disponibles: disponiblesDeEstadoDeMarca(e),
      fecha: baja ?? renov,
      fechaEs: baja ? 'baja' : renov ? 'renovacion' : null,
      cobroRechazado,
      puedeCancelar: tienePlan && !baja,
      cobro,
      puedeCambiarTarjeta: tienePlan && (!baja || cobro?.tipo === 'baja_programada'),
      cobroPendiente: !!cobro || cobroRechazado,
      // Con la baja agendada el servidor ya no recarga (`necesitaRecarga`): se muestra detenida.
      recarga: tienePlan ? recargaConBaja(recargaDeEstado(e?.recarga), !!baja, b?.trigger_cancelacion === 'marca_cancelar') : null,
    };
  });
}

export const MOTIVO_PLAN_ACTIVO = 'Cancelá su plan antes de borrarla.';

/**
 * ¿Se puede borrar una marca? Con facturación por marca, no si su billetera tiene plan o
 * está dada de baja en Rebill pero sin terminar. `null` = se puede. Sin billetera, se puede.
 */
export function motivoDeBorradoBloqueado(
  b: { plan: string | null; status: string | null } | undefined,
): string | null {
  if (!b) return null;
  if (b.plan != null || b.status === 'baja_pendiente_en_rebill') return MOTIVO_PLAN_ACTIVO;
  return null;
}

/**
 * El texto de un error al borrar una marca. El trigger de la base (`P0001`, mismo texto que
 * `MOTIVO_PLAN_ACTIVO`) frena el borrado con plan vivo aunque el front no lo haya visto; la
 * billetera sin plan se va en cascada, así que la FK ya no frena nada (ADR ...-2).
 */
export function mensajeDeErrorAlBorrarMarca(e: unknown): string {
  const err = e as { code?: string; message?: string } | null;
  if (err?.code === 'P0001' && /Cancelá su plan/i.test(err.message ?? '')) return MOTIVO_PLAN_ACTIVO;
  return e instanceof Error ? e.message : err?.message || 'No se pudo eliminar la marca. Probá de nuevo.';
}

const ERRORES_CANCELAR: Record<string, string> = {
  no_es_el_dueno: 'Sólo el admin de la cuenta puede cancelar el plan de una marca.',
  sin_plan: 'Esta marca no tiene plan para cancelar.',
  sin_bandera: 'Esta cuenta no tiene planes por marca. Escribinos y lo revisamos.',
  marca_ajena: 'Esta marca no es de tu cuenta. Actualizá la página y probá de nuevo.',
  unauthorized: 'No pudimos validar tu sesión. Cerrá sesión, volvé a entrar y probá de nuevo.',
};

export function mensajeDeErrorAlCancelarMarca(error: string): string {
  return ERRORES_CANCELAR[error] ?? 'No pudimos cancelar el plan. Probá de nuevo en unos minutos.';
}

const ERRORES_CAMBIAR_TARJETA: Record<string, string> = {
  sin_suscripcion: 'Esta marca no tiene una suscripción con tarjeta para cambiar. Escribinos y lo revisamos.',
  no_es_el_dueno: 'Sólo el admin de la cuenta puede cambiar la tarjeta de una marca.',
  marca_ajena: 'Esta marca no es de tu cuenta. Actualizá la página y probá de nuevo.',
  unauthorized: 'No pudimos validar tu sesión. Cerrá sesión, volvé a entrar y probá de nuevo.',
  rebill_unavailable: 'El procesador de pagos no respondió. Probá de nuevo en unos minutos.',
};

/** El texto de un rechazo de `/marca/cambiar-tarjeta` (ADR-2026-10-29-cobro-rechazado-por-marca). */
export function mensajeDeErrorAlCambiarTarjetaDeMarca(error: string): string {
  return ERRORES_CAMBIAR_TARJETA[error] ?? 'No pudimos abrir el cambio de tarjeta. Probá de nuevo en unos minutos.';
}

/**
 * Qué muestra "Facturación de tu plan" arriba de los datos fiscales
 * (ADR-2026-10-29-facturacion-con-planes-por-marca):
 *  - `empresa`: la suscripción de la empresa, como siempre (sin bandera, o el `?demo`).
 *  - `marcas`: los planes de cada marca (`PlanPorMarca`); nada de la suscripción de la empresa.
 *  - `sin_saber`: no se pudo leer la bandera. Ni la tarjeta ni los botones de la empresa: con la
 *    bandera prendida venderían o cancelarían una suscripción que no existe.
 */
export type BloqueDeSuscripcion = 'empresa' | 'marcas' | 'sin_saber';

export function bloqueDeSuscripcion(e: { demo: boolean; facturaPorMarca: boolean | null | undefined }): BloqueDeSuscripcion {
  if (e.demo) return 'empresa';
  if (e.facturaPorMarca === true) return 'marcas';
  if (e.facturaPorMarca === false) return 'empresa';
  return 'sin_saber';
}

/**
 * El nombre de la marca de cada pago (`payments.brand_id` → `brand_profiles.name`), para poner la
 * marca en cada factura del historial. Un pago sin marca, o con una marca que ya no está, no entra.
 */
export function marcaDeCadaPago(
  pagos: { id: string; brand_id: string | null }[],
  marcas: { id: string; name: string }[],
): Map<string, string> {
  const nombre = new Map(marcas.map((m) => [m.id, m.name]));
  const r = new Map<string, string>();
  for (const p of pagos) {
    const n = p.brand_id ? nombre.get(p.brand_id) : undefined;
    if (n) r.set(p.id, n);
  }
  return r;
}

/**
 * ¿Hay que (re)cargar la marca de las facturas? Sí cuando la bandera se sabe prendida, la carga
 * terminó, todavía no se pidieron con la bandera prendida y hay pagos que buscar. Pasa si la
 * primera lectura del saldo tardó de más (sin saldo guardado): `fetchTodo` armó el historial sin
 * marcas y la bandera llegó después por `useUltimoSaldo` (review Greptile #218).
 */
export function hayQueCargarMarcasDeFacturas(e: {
  facturaPorMarca: boolean | null | undefined;
  cargando: boolean;
  marcasPedidas: boolean;
  idsDePago: string[];
}): boolean {
  return e.facturaPorMarca === true && !e.cargando && !e.marcasPedidas && e.idsDePago.length > 0;
}

/** Las facturas con la marca de su pago; la que no aparece en `marcaDelPago` queda como estaba. */
export function conMarcaDeCadaPago<F extends { paymentId?: string | null; marca?: string | null }>(
  facturas: F[],
  marcaDelPago: Map<string, string>,
): F[] {
  return facturas.map((f) => {
    const marca = f.paymentId ? marcaDelPago.get(f.paymentId) : undefined;
    return marca ? { ...f, marca } : f;
  });
}
